/* eslint-disable react/no-unescaped-entities, react-hooks/set-state-in-effect, react-hooks/immutability -- legacy signing page: cosmetic/experimental rules, fonctionnel et validé en prod */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    FileText, CheckCircle, PenTool, AlertCircle, 
    Home, Euro, Calendar, User, Shield, Loader2,
    Download, ArrowRight, Check
} from 'lucide-react';
import api from '../../lib/api';

export default function OfferSignPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [loading, setLoading] = useState(true);
    const [offer, setOffer] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState('review'); // review, sign, success
    const [signing, setSigning] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);
    
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);

    useEffect(() => {
        if (token) {
            loadOffer();
        } else {
            setError("Token manquant");
            setLoading(false);
        }
    }, [token]);

    const loadOffer = async () => {
        try {
            // Find offer by signing token
            const response = await api.get(`/offers/by-token/${token}`);
            setOffer(response.data);
            
            // Check if current buyer has already signed
            if (response.data.current_buyer?.has_signed) {
                setStep('success');
            }
        } catch (err) {
            setError("Offre non trouvée ou lien expiré");
        } finally {
            setLoading(false);
        }
    };

    // Canvas drawing functions
    const startDrawing = (e) => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
        const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
        
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
        const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
        
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineTo(x, y);
        ctx.stroke();
        setHasSignature(true);
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
    };

    const submitSignature = async () => {
        if (!hasSignature || !acceptTerms) return;
        
        setSigning(true);
        try {
            const canvas = canvasRef.current;
            const signatureData = {
                signature_image: canvas.toDataURL('image/png'),
                signed_at: new Date().toISOString(),
                user_agent: navigator.userAgent
            };

            await api.post('/offers/sign', signatureData, {
                params: { token }
            });

            setStep('success');
        } catch (err) {
            setError(err.response?.data?.detail || "Erreur lors de la signature");
        } finally {
            setSigning(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground">Chargement de l'offre...</p>
                </div>
            </div>
        );
    }

    if (error && !offer) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
                <div className="bg-muted backdrop-blur-xl rounded-3xl p-8 max-w-md w-full text-center border border-white/20">
                    <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-white mb-2">Erreur</h1>
                    <p className="text-muted-foreground">{error}</p>
                </div>
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
                <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-muted backdrop-blur-xl rounded-3xl p-8 max-w-md w-full text-center border border-white/20"
                >
                    <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-2">Offre signée !</h1>
                    <p className="text-muted-foreground mb-6">
                        Votre offre d'achat a été signée électroniquement et transmise au vendeur.
                    </p>
                    <div className="bg-card rounded-2xl p-4 mb-6">
                        <p className="text-sm text-muted-foreground mb-1">Montant de l'offre</p>
                        <p className="text-3xl font-bold text-white">
                            {offer?.offer_amount?.toLocaleString('fr-FR')} €
                        </p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        Vous recevrez une notification dès que le vendeur aura répondu à votre offre.
                    </p>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
            {/* Header */}
            <div className="bg-card backdrop-blur-xl border-b border-border">
                <div className="max-w-4xl mx-auto px-4 py-6">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-orange-500 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-white">Offre d'achat</h1>
                            <p className="text-muted-foreground text-sm">Signature électronique sécurisée</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Progress */}
                <div className="flex items-center justify-center gap-4 mb-8">
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step === 'review' ? 'bg-purple-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                        <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm">1</span>
                        <span className="font-medium">Vérification</span>
                    </div>
                    <ArrowRight className="w-5 h-5 text-white/30" />
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step === 'sign' ? 'bg-purple-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                        <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm">2</span>
                        <span className="font-medium">Signature</span>
                    </div>
                </div>

                <AnimatePresence mode="wait">
                    {step === 'review' && (
                        <motion.div
                            key="review"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="space-y-6"
                        >
                            {/* Offer summary */}
                            <div className="bg-muted backdrop-blur-xl rounded-3xl p-6 border border-white/20">
                                <h2 className="text-lg font-bold text-white mb-4">Récapitulatif de l'offre</h2>
                                
                                {/* Amount highlight */}
                                <div className="bg-gradient-to-r from-purple-500 to-orange-500 rounded-2xl p-6 text-center mb-6">
                                    <p className="text-white/80 text-sm mb-1">Montant de votre offre</p>
                                    <p className="text-4xl font-black text-white">
                                        {offer?.offer_amount?.toLocaleString('fr-FR')} €
                                    </p>
                                </div>

                                {/* Property info */}
                                <div className="grid md:grid-cols-2 gap-4 mb-6">
                                    <div className="bg-card rounded-2xl p-4">
                                        <div className="flex items-center gap-3 mb-2">
                                            <Home className="w-5 h-5 text-purple-400" />
                                            <span className="text-muted-foreground text-sm">Bien concerné</span>
                                        </div>
                                        <p className="text-white font-medium">
                                            {offer?.mandate?.property?.address?.street}
                                        </p>
                                        <p className="text-muted-foreground text-sm">
                                            {offer?.mandate?.property?.address?.postal_code} {offer?.mandate?.property?.address?.city}
                                        </p>
                                    </div>
                                    <div className="bg-card rounded-2xl p-4">
                                        <div className="flex items-center gap-3 mb-2">
                                            <Euro className="w-5 h-5 text-orange-400" />
                                            <span className="text-muted-foreground text-sm">Prix affiché</span>
                                        </div>
                                        <p className="text-white font-medium">
                                            {offer?.mandate?.price?.toLocaleString('fr-FR') || 'Non renseigné'} €
                                        </p>
                                        {offer?.mandate?.price && offer?.offer_amount && (
                                            <p className={`text-sm ${offer.offer_amount < offer.mandate.price ? 'text-green-400' : 'text-orange-400'}`}>
                                                {offer.offer_amount < offer.mandate.price 
                                                    ? `-${((1 - offer.offer_amount / offer.mandate.price) * 100).toFixed(1)}% par rapport au prix`
                                                    : offer.offer_amount === offer.mandate.price 
                                                        ? 'Au prix affiché'
                                                        : `+${((offer.offer_amount / offer.mandate.price - 1) * 100).toFixed(1)}% au-dessus du prix`
                                                }
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Financing */}
                                <div className="bg-card rounded-2xl p-4 mb-6">
                                    <h3 className="font-bold text-white mb-3">Financement</h3>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <p className="text-muted-foreground">Type</p>
                                            <p className="text-white font-medium">
                                                {offer?.financing?.type === 'credit' ? 'Crédit immobilier' : 
                                                 offer?.financing?.type === 'cash' ? 'Comptant' : 'Mixte'}
                                            </p>
                                        </div>
                                        {offer?.financing?.type !== 'cash' && (
                                            <>
                                                <div>
                                                    <p className="text-muted-foreground">Apport</p>
                                                    <p className="text-white font-medium">
                                                        {offer?.financing?.personal_contribution?.toLocaleString('fr-FR')} €
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-muted-foreground">Montant du prêt</p>
                                                    <p className="text-white font-medium">
                                                        {offer?.financing?.loan_amount?.toLocaleString('fr-FR')} €
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-muted-foreground">Durée</p>
                                                    <p className="text-white font-medium">
                                                        {offer?.financing?.loan_duration_months} mois
                                                    </p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Validity */}
                                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-4">
                                    <div className="flex items-center gap-3">
                                        <Calendar className="w-5 h-5 text-yellow-400" />
                                        <div>
                                            <p className="text-white font-medium">Validité de l'offre</p>
                                            <p className="text-muted-foreground text-sm">
                                                Cette offre est valable {offer?.validity_days} jours à compter de sa signature
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* PDF Preview link */}
                            <a 
                                href={`/api/offers/${offer?.id}/pdf`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="offer-pdf-link"
                                className="flex items-center justify-center gap-2 w-full py-4 bg-card hover:bg-muted rounded-2xl text-white/80 hover:text-white transition-colors border border-border"
                            >
                                <Download className="w-5 h-5" />
                                <span>Voir / imprimer le document complet</span>
                            </a>

                            {/* Continue button */}
                            <button
                                onClick={() => setStep('sign')}
                                className="w-full py-4 bg-gradient-to-r from-purple-500 to-orange-500 hover:opacity-90 rounded-2xl text-white font-bold flex items-center justify-center gap-2 transition-opacity"
                            >
                                <span>Continuer vers la signature</span>
                                <ArrowRight className="w-5 h-5" />
                            </button>
                        </motion.div>
                    )}

                    {step === 'sign' && (
                        <motion.div
                            key="sign"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="space-y-6"
                        >
                            <div className="bg-muted backdrop-blur-xl rounded-3xl p-6 border border-white/20">
                                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                    <PenTool className="w-5 h-5 text-purple-400" />
                                    Votre signature
                                </h2>
                                
                                {/* Signature canvas */}
                                <div className="bg-white rounded-2xl p-4 mb-4">
                                    <canvas
                                        ref={canvasRef}
                                        width={500}
                                        height={200}
                                        className="w-full border-2 border-dashed border-gray-300 rounded-xl cursor-crosshair touch-none"
                                        onMouseDown={startDrawing}
                                        onMouseMove={draw}
                                        onMouseUp={stopDrawing}
                                        onMouseLeave={stopDrawing}
                                        onTouchStart={startDrawing}
                                        onTouchMove={draw}
                                        onTouchEnd={stopDrawing}
                                    />
                                    <p className="text-center text-muted-foreground text-sm mt-2">
                                        Dessinez votre signature ci-dessus
                                    </p>
                                </div>

                                <button
                                    onClick={clearSignature}
                                    className="text-sm text-muted-foreground hover:text-white underline"
                                >
                                    Effacer et recommencer
                                </button>
                            </div>

                            {/* Terms acceptance */}
                            <div className="bg-muted backdrop-blur-xl rounded-3xl p-6 border border-white/20">
                                <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-checkbox-label">
                                    <div 
                                        data-testid="terms-checkbox"
                                        className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center mt-0.5 transition-colors ${
                                            acceptTerms ? 'bg-purple-500 border-purple-500' : 'border-white/30'
                                        }`}
                                        onClick={() => setAcceptTerms(!acceptTerms)}
                                    >
                                        {acceptTerms && <Check className="w-4 h-4 text-white" />}
                                    </div>
                                    <span className="text-white/80 text-sm">
                                        J'ai lu et j'accepte les conditions de l'offre d'achat. Je comprends que cette signature électronique 
                                        a la même valeur juridique qu'une signature manuscrite conformément au règlement eIDAS.
                                    </span>
                                </label>
                            </div>

                            {/* Security badge */}
                            <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm">
                                <Shield className="w-4 h-4" />
                                <span>Signature électronique sécurisée</span>
                            </div>

                            {/* Error message */}
                            {error && (
                                <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 text-red-400">
                                    {error}
                                </div>
                            )}

                            {/* Buttons */}
                            <div className="flex gap-4">
                                <button
                                    onClick={() => setStep('review')}
                                    data-testid="back-btn"
                                    className="flex-1 py-4 bg-card hover:bg-muted rounded-2xl text-white font-medium transition-colors"
                                >
                                    Retour
                                </button>
                                <button
                                    onClick={submitSignature}
                                    disabled={!hasSignature || !acceptTerms || signing}
                                    data-testid="sign-btn"
                                    className={`flex-1 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all ${
                                        hasSignature && acceptTerms && !signing
                                            ? 'bg-gradient-to-r from-purple-500 to-orange-500 hover:opacity-90 text-white'
                                            : 'bg-muted text-muted-foreground cursor-not-allowed'
                                    }`}
                                >
                                    {signing ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            <span>Signature en cours...</span>
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="w-5 h-5" />
                                            <span>Signer l'offre</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
